import React, { useMemo, useEffect, useState } from 'react';
import { Select } from 'components/Common/Select';
import { convertVisaOptions } from './utils';
import { isNil } from 'lodash';
import { getVisaTypes as getVisaTypesAction } from 'store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import i18n from 'i18n';
import styled from 'styled-components';
import { HelpText } from 'components/HelpText';

// 1. country changes → fetch countryVisaTypes (Redux)
// 2. nationality changes → fetch countryNationalityVisaTypes (local state)
// 3. visasTypes prop provided → use it directly (priority)
// 4. If no visasTypes, use countryNationalityVisaTypes
// 5. If no countryNationalityVisaTypes, use countryVisaTypes (Redux)
// 6. Warning checks against all sources to find visa type name

const getVisaTypesState = state => state.references.visaTypes;
const getCountriesState = state => state.references.countries;

// Styled components for warning
const WarningWrapper = styled.div`
  margin-top: 8px;
  width: 100%;
`;

const VisaTypeSelectorWrapper = styled.div`
  width: 100%;
`;

const WarningContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const WarningTitle = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const WarningDescription = styled.span`
  font-size: 12px;
  color: #475569;
  line-height: 1.5;
`;

const ClearButton = styled.button`
  margin-top: 4px;
  padding: 6px 16px;
  border-radius: 6px;
  border: 1px solid #f59e0b;
  background: transparent;
  color: #d97706;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #fffbeb;
    border-color: #d97706;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  i {
    font-size: 14px;
  }
`;

// Helper to get country name from ID
const getCountryNameFromId = (countryId, countries) => {
  if (!countryId) return null;

  if (typeof countryId === 'string' && !countryId.match(/^\d+$/)) {
    return countryId;
  }

  if (typeof countryId === 'object') {
    return countryId.name || countryId.country?.name || countryId.id;
  }

  if (countries && Array.isArray(countries)) {
    const found = countries.find(c => c.id === countryId);
    if (found) {
      return found.country?.name || found.name || found.id;
    }
  }

  return null;
};

// Helper to get visa type name from ANY available source
const getVisaTypeName = (visaId, visaTypesList) => {
  if (!visaId) return null;

  if (typeof visaId === 'string' && !visaId.match(/^\d+$/)) {
    return visaId;
  }

  if (typeof visaId === 'object') {
    return (
      visaId.translations?.[i18n.language] ||
      visaId.name ||
      visaId.label ||
      visaId.id
    );
  }

  if (visaTypesList && Array.isArray(visaTypesList)) {
    const found = visaTypesList.find(
      vt => vt.id === visaId || vt.value === visaId,
    );
    if (found) {
      return (
        found.translations?.[i18n.language] ||
        found.name ||
        found.label ||
        found.id
      );
    }
  }

  return null;
};

export const VisaTypeSelector = ({
  value,
  onChange,
  error,
  isDisabled,
  country,
  applicantNationality,
  visasTypes,
  countryName: propCountryName = '',
  nationalityName: propNationalityName = '',
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const countryVisaTypes = useSelector(getVisaTypesState);
  const countries = useSelector(getCountriesState);
  const [showWarning, setShowWarning] = useState(false);
  const [cachedVisaTypeName, setCachedVisaTypeName] = useState(null);

  // Local state for visa types filtered by country + nationality
  const [countryNationalityVisaTypes, setCountryNationalityVisaTypes] =
    useState([]);

  // Fetch 1: Only country (stored in Redux) - ALWAYS fetch regardless of visasTypes
  useEffect(() => {
    if (country) {
      dispatch(
        getVisaTypesAction({
          country: country,
        }),
      );
    }
  }, [country, dispatch]);

  // Fetch 2: Country + Nationality (stored in local state) - only if not provided via props
  useEffect(() => {
    if (!visasTypes && country && applicantNationality) {
      dispatch(
        getVisaTypesAction({
          country: country,
          valid_nationalities: applicantNationality,
        }),
      );
    }
  }, [country, applicantNationality, visasTypes, dispatch]);

  // Update local state when Redux data changes (for nationality-filtered list)
  useEffect(() => {
    if (countryVisaTypes && countryVisaTypes.length > 0) {
      if (country && applicantNationality) {
        setCountryNationalityVisaTypes(countryVisaTypes);
      }
    }
  }, [countryVisaTypes, country, applicantNationality]);

  // Use the appropriate list for options
  const visaTypesList = useMemo(() => {
    // Priority: props > country+nationality filtered > country-only (Redux)
    if (visasTypes) return visasTypes;
    if (countryNationalityVisaTypes.length > 0)
      return countryNationalityVisaTypes;
    return countryVisaTypes || [];
  }, [visasTypes, countryNationalityVisaTypes, countryVisaTypes]);

  // Convert options from the filtered list
  const allOptions = useMemo(() => {
    return convertVisaOptions(visaTypesList);
  }, [visaTypesList]);

  // Find the selected value in options (will be null if filtered out)
  const val = useMemo(() => {
    if (isNil(value)) {
      return value;
    }

    return allOptions.find(item => item.value === value) ?? null;
  }, [allOptions, value, i18n.language]);

  // Get visa type name from ANY available source
  const visaTypeName = useMemo(() => {
    // Try all sources in order
    const name = getVisaTypeName(value, visaTypesList);
    if (name) return name;

    // Try country-only as fallback
    const nameFromCountry = getVisaTypeName(value, countryVisaTypes);
    if (nameFromCountry) return nameFromCountry;

    // Try country+nationality as fallback
    const nameFromCountryNationality = getVisaTypeName(
      value,
      countryNationalityVisaTypes,
    );
    if (nameFromCountryNationality) return nameFromCountryNationality;

    // If not found and we have a cached name, use that
    if (cachedVisaTypeName) return cachedVisaTypeName;

    return null;
  }, [
    value,
    visaTypesList,
    countryVisaTypes,
    countryNationalityVisaTypes,
    cachedVisaTypeName,
  ]);

  // Get translated country name
  const resolvedCountryName = useMemo(() => {
    if (
      propCountryName &&
      typeof propCountryName === 'string' &&
      !propCountryName.match(/^\d+$/)
    ) {
      return propCountryName;
    }

    const nameFromStore = getCountryNameFromId(country, countries);
    if (nameFromStore) return nameFromStore;

    if (country && typeof country === 'object') {
      return country.name || country.country?.name || null;
    }

    return null;
  }, [country, countries, propCountryName]);

  // Get translated nationality name
  const resolvedNationalityName = useMemo(() => {
    if (
      propNationalityName &&
      typeof propNationalityName === 'string' &&
      !propNationalityName.match(/^\d+$/)
    ) {
      return propNationalityName;
    }

    const nameFromStore = getCountryNameFromId(applicantNationality, countries);
    if (nameFromStore) return nameFromStore;

    if (applicantNationality && typeof applicantNationality === 'object') {
      return (
        applicantNationality.name || applicantNationality.country?.name || null
      );
    }

    return null;
  }, [applicantNationality, countries, propNationalityName]);

  // Check if the selected visa type is invalid
  useEffect(() => {
    if (value && !val) {
      setShowWarning(true);

      // Try to get the name from any source
      const name = getVisaTypeName(value, visaTypesList);
      if (name) {
        setCachedVisaTypeName(name);
      } else if (!cachedVisaTypeName) {
        setCachedVisaTypeName(`Visa Type #${value}`);
      }
    } else {
      setShowWarning(false);
      if (val) {
        setCachedVisaTypeName(null);
      }
    }
  }, [value, val, visaTypesList]);

  // Filter active options
  const options = useMemo(() => {
    return allOptions.filter(option => option.is_active === true);
  }, [allOptions]);

  // Handle clearing the invalid selection
  const handleClearInvalidSelection = () => {
    onChange(null);
    setShowWarning(false);
    setCachedVisaTypeName(null);
  };

  return (
    <VisaTypeSelectorWrapper>
      <Select
        className="w-100"
        classNamePrefix="react-select"
        placeholder={t('order.visaCategoryPlaceholder')}
        value={val}
        isDisabled={isDisabled || showWarning}
        isClearable
        onChange={onChange}
        options={options}
        invalid={Boolean(error) || showWarning}
      />

      {/* Show warning when selected visa type is not available */}
      {showWarning && (
        <WarningWrapper>
          <HelpText
            variant="warning"
            size="sm"
            showLeftBorder
            leftBorderColor="#f59e0b"
            hideIcon
            gap="8px"
            align="flex-start"
          >
            <WarningContent>
              <WarningTitle>
                <i
                  className="bx bx-error-circle"
                  style={{ color: '#f59e0b' }}
                />
                {t('order.visaTypeNotAvailable')}
              </WarningTitle>

              <WarningDescription>
                <Trans
                  i18nKey="order.visaTypeNotAvailableDescription"
                  values={{
                    visaType:
                      visaTypeName ||
                      cachedVisaTypeName ||
                      `Visa Type #${value}`,
                    country: resolvedCountryName || 'Unknown',
                    nationality: resolvedNationalityName || 'Unknown',
                  }}
                  components={{
                    1: <strong style={{ color: '#dc2626' }} />,
                  }}
                />
              </WarningDescription>

              <ClearButton onClick={handleClearInvalidSelection}>
                <i className="bx bx-x" />
                {t('order.clearSelection')}
              </ClearButton>
            </WarningContent>
          </HelpText>
        </WarningWrapper>
      )}

      {/* Show error from formik validation if any */}
      {error && !showWarning && (
        <WarningWrapper>
          <HelpText variant="danger" noBackground>
            {error}
          </HelpText>
        </WarningWrapper>
      )}
    </VisaTypeSelectorWrapper>
  );
};
